import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, INR, INR2 } from "@/lib/api";
import { Bell, Gift, Coins, TrendingUp, TrendingDown, Landmark, ArrowUpRight, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DepositModal from "@/components/modals/DepositModal";
import GiftModal from "@/components/modals/GiftModal";
import PledgeModal from "@/components/modals/PledgeModal";
import Sparkline from "@/components/Sparkline";
import MonthDots from "@/components/MonthDots";
import LiveRateTicker from "@/components/LiveRateTicker";
import MetalToggle from "@/components/MetalToggle";

export default function Home() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [price, setPrice] = useState(null);
  const [history, setHistory] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [kitty, setKitty] = useState(null);
  const [metal, setMetal] = useState("gold");
  const [openDeposit, setOpenDeposit] = useState(false);
  const [openGift, setOpenGift] = useState(false);
  const [openPledge, setOpenPledge] = useState(false);

  const refresh = async (m = metal) => {
    const [s, p, h, n, k] = await Promise.all([
      api.get("/wallet/summary"),
      api.get("/market/price"),
      api.get(`/market/history?days=30&metal=${m}`),
      api.get("/notifications"),
      api.get("/kitty/list"),
    ]);
    setSummary(s.data);
    setPrice(p.data);
    setHistory(h.data.history || []);
    setNotifs(n.data.notifications || []);
    const list = k.data.enrollments || [];
    setKitty(list[0] || null);
  };

  useEffect(() => {
    refresh(metal);
    const t = setInterval(() => refresh(metal), 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metal]);

  const unread = notifs.filter((n) => !n.read).length;
  const isGold = metal === "gold";
  const holding = isGold ? summary?.gold : summary?.silver;
  const rate = isGold ? price?.price_24k_inr_per_gram : price?.price_silver_inr_per_gram;
  const changePct = isGold ? price?.change_pct_24h : price?.change_pct_24h_silver;
  const up = (changePct || 0) >= 0;
  const priceSeries = history
    .map((h) => (isGold ? h.price_24k : h.price_silver))
    .filter((v) => Number.isFinite(v));

  return (
    <div className="pt-6 relative z-10">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold tracking-[0.24em] uppercase text-brand-primary">Namaste</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-brand-cream">
            {user?.name?.split(" ")[0] || "Friend"}
          </h1>
        </div>
        <Link
          to="/notifications"
          data-testid="home-bell"
          className="relative rounded-full h-11 w-11 bg-brand-sand2 border border-brand-line-faint flex items-center justify-center active:scale-95 transition-transform"
        >
          <Bell size={18} className="text-brand-cream" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-brand-primary text-brand-page text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </Link>
      </div>

      {/* Metal toggle */}
      <div className="mt-5">
        <MetalToggle value={metal} onChange={setMetal} />
      </div>

      {/* Vault hero card */}
      <div
        data-testid="home-balance-card"
        className="mt-4 rounded-3xl bg-gradient-to-b from-brand-sand2 to-brand-sand p-6 border border-brand-line shadow-card relative overflow-hidden"
      >
        <div className={`absolute -right-16 -top-16 h-52 w-52 rounded-full blur-3xl ${isGold ? "bg-brand-primary/12" : "bg-slate-300/10"}`} />
        <div className={`absolute left-0 bottom-0 h-24 w-24 rounded-full blur-2xl ${isGold ? "bg-brand-accent/10" : "bg-slate-200/10"}`} />

        <div className={`text-[11px] uppercase tracking-[0.24em] ${isGold ? "text-brand-primary" : "text-slate-300"}`}>
          Your {isGold ? "Gold" : "Silver"} Vault
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span
            className={`font-display text-6xl font-semibold tabular-nums leading-none ${
              isGold ? "text-gold-gradient" : "bg-gradient-to-r from-slate-100 via-zinc-200 to-slate-400 bg-clip-text text-transparent"
            }`}
          >
            {holding ? Number(holding.grams).toFixed(3) : "—"}
          </span>
          <span className={`font-display italic text-2xl ${isGold ? "text-brand-primary" : "text-slate-300"}`}>g</span>
        </div>
        <div className="mt-1 text-brand-slate tabular-nums text-sm">
          ≈ {holding ? INR(holding.inr_value) : "—"}
        </div>
        <div className="mt-1 text-[11px] text-brand-slate2">
          {isGold ? "24K BIS certified · MMTC-PAMP vault" : "999 fine silver · MMTC-PAMP vault"}
        </div>

        {priceSeries.length > 0 && (
          <div className="mt-4 -mx-1">
            <Sparkline data={priceSeries} height={54} color={isGold ? "#E8B84B" : "#D4D4D8"} />
            <div className="mt-1 flex justify-between text-[10px] text-brand-slate2 tabular-nums">
              <span>{history[0]?.date?.slice(5)}</span>
              <span>Today</span>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <div className="rounded-full bg-brand-sand border border-brand-line-faint px-3 py-1 text-brand-cream tabular-nums text-xs">
            {isGold ? "24k" : "Silver"} · {rate ? INR2(rate) : "—"}/g
          </div>
          <div
            className={`rounded-full px-2.5 py-1 flex items-center gap-1 text-[11px] font-bold ${
              up ? "bg-emerald-900/30 text-brand-secondary" : "bg-red-900/30 text-brand-danger"
            }`}
          >
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {changePct != null ? changePct : 0}%
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        <QuickAction tid="qa-deposit" Icon={Coins} label="Buy" onClick={() => setOpenDeposit(true)} />
        <QuickAction tid="qa-gift" Icon={Gift} label="Gift" onClick={() => setOpenGift(true)} />
        <QuickAction tid="qa-pledge" Icon={Landmark} label="Pledge" onClick={() => setOpenPledge(true)} />
      </div>

      {/* Live ticker */}
      <div className="mt-5">
        <LiveRateTicker />
      </div>

      {/* Kitty progress */}
      <div className="mt-6 rounded-3xl bg-brand-sand border border-brand-line-faint p-5 shadow-soft">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-primary font-bold">Kitty · Digital SIP</div>
            <h3 className="mt-1 font-display text-2xl text-brand-cream">
              {kitty?.plan_name || "Start your first kitty"}
            </h3>
            <div className="mt-1 text-xs text-brand-slate">
              {kitty
                ? `${kitty.frequency} · ${INR(kitty.amount_inr)} · ${(kitty.installments_paid || 0)}/11 done`
                : "Save daily/weekly/monthly. Month 12 gold is FREE ★"}
            </div>
          </div>
          <Link to="/save" data-testid="home-save-cta" className="text-brand-primary text-sm font-semibold underline underline-offset-4">
            {kitty ? "Manage" : "Enroll"}
          </Link>
        </div>
        <div className="mt-4">
          <MonthDots done={kitty?.installments_paid || 0} total={11} />
        </div>
        {kitty && (
          <div className="mt-3 text-[11px] text-brand-slate2">
            ★ Month 12 is FREE — jeweller adds bonus gold worth {INR(kitty.amount_inr)}
          </div>
        )}
      </div>

      {/* Redeem card */}
      <Link
        to="/redeem"
        data-testid="home-redeem-cta"
        className="mt-4 block rounded-3xl border border-brand-line-faint bg-brand-sand p-5 shadow-soft active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-primary font-bold">Redeem</div>
            <div className="mt-1 font-display text-xl text-brand-cream">
              Marriage · Medical · Education · Home
            </div>
            <div className="text-[11px] text-brand-slate2 mt-1">Pledge or convert to cash · 6 partner jewellers</div>
          </div>
          <ArrowUpRight className="text-brand-primary" />
        </div>
      </Link>

      {/* ETF promo card */}
      <Link
        to="/market"
        state={{ mode: "etf" }}
        data-testid="home-etf-cta"
        className="mt-4 block rounded-3xl border border-brand-line-faint bg-brand-sand p-5 shadow-soft active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary font-bold">Gold ETFs</div>
            <div className="mt-1 font-display text-xl text-brand-cream">
              25 govt-backed funds · SEBI regulated
            </div>
            <div className="text-[11px] text-brand-slate2 mt-1">Buy units from GOLDBEES, ICICIGOLD, HDFCGOLD & more</div>
          </div>
          <ArrowUpRight className="text-brand-primary" />
        </div>
      </Link>

      {/* Trust footer */}
      <div className="mt-6 mb-4 rounded-3xl bg-brand-sand2 border border-brand-line-faint p-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-brand-primary/15 flex items-center justify-center">
          <ShieldCheck className="text-brand-primary" size={20} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-brand-cream">SEBI · BIS · MMTC-PAMP</div>
          <div className="text-[11px] text-brand-slate">Your holdings are insured, hallmarked, and vaulted.</div>
        </div>
        <Sparkles size={16} className="text-brand-accent" />
      </div>

      <DepositModal open={openDeposit} onOpenChange={setOpenDeposit} onDone={() => refresh(metal)} defaultMetal={metal} />
      <GiftModal open={openGift} onOpenChange={setOpenGift} onDone={() => refresh(metal)} balance={summary?.grams || 0} />
      <PledgeModal open={openPledge} onOpenChange={setOpenPledge} onDone={() => refresh(metal)} balance={summary?.grams || 0} />
    </div>
  );
}

function QuickAction({ Icon, label, onClick, tid }) {
  return (
    <button
      data-testid={tid}
      onClick={onClick}
      className="rounded-2xl border border-brand-line-faint bg-brand-sand p-4 flex flex-col items-center gap-2 shadow-soft active:scale-[0.96] transition-transform hover:border-brand-line"
    >
      <div className="h-11 w-11 rounded-full bg-gold-gradient text-brand-page flex items-center justify-center shadow-gold">
        <Icon size={18} />
      </div>
      <div className="text-sm font-semibold text-brand-cream">{label}</div>
    </button>
  );
}
